import { describeRpcError, isAuthorizationError } from "@how/app-kit";
import { Badge, Button, Card, Checkbox, Group, Stack, Text, Textarea, Title } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useRef, useState } from "react";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PermissionDenied,
  DISPLAY_LOCALE,
} from "@how/ui";
import { useMyCareRequests, useSubmitCareRequest, type MyCareRequest } from "./care-queries";
import { semanticColor, type SemanticColor } from "@how/design-system";

function statusWords(r: MyCareRequest): { label: string; color: SemanticColor } {
  if (r.status === "closed") {
    const when =
      r.closedAt === null
        ? ""
        : ` ${new Date(r.closedAt).toLocaleDateString(DISPLAY_LOCALE, { day: "numeric", month: "short" })}`;
    return { label: `Closed${when}`, color: semanticColor("neutral") };
  }
  if (r.status === "in_progress")
    return { label: "Being looked after", color: semanticColor("success") };
  if (r.hasOwner) return { label: "Someone is looking after it", color: semanticColor("success") };
  return { label: "Received", color: semanticColor("info") };
}

/**
 * Care (task 15, the member's half): ask for prayer or help, and know it arrived. The person is resolved from the
 * login on the server; the screen sends words and two choices.
 */
export function CarePage() {
  const mine = useMyCareRequests();
  const submit = useSubmitCareRequest();
  const [body, setBody] = useState("");
  const [mayContact, setMayContact] = useState(true);
  const [confidential, setConfidential] = useState(false);
  // Sending clears the words, which disables Send, the button pressed: focus goes back to the words (issue #285).
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  return (
    <>
      <PageHeader
        title="Care"
        description="Ask for prayer or for help. Your church sees it straight away, and you see here where it stands."
      />
      {mine.isPending ? (
        <LoadingState rows={4} />
      ) : mine.isError ? (
        isAuthorizationError(mine.error) || describeRpcError(mine.error).kind === "denied" ? (
          <PermissionDenied />
        ) : (
          <ErrorState
            message="Could not load your requests."
            onRetry={() => {
              void mine.refetch();
            }}
          />
        )
      ) : !mine.data.linked ? (
        <EmptyState
          title="No record is linked to your login yet"
          description="Ask your church to link your login to your record; you can ask for prayer or help after that."
        />
      ) : (
        <Stack gap="xl">
          <Card component="section" aria-labelledby="care-ask">
            <Stack gap="sm">
              <Title order={2} size="h4" id="care-ask">
                Ask for prayer or help
              </Title>
              <Textarea
                ref={bodyRef}
                label="What is going on"
                placeholder="Please pray for… / Could someone…"
                autosize
                minRows={3}
                maxLength={4000}
                value={body}
                onChange={(e) => {
                  setBody(e.currentTarget.value);
                }}
              />
              <Checkbox
                label="You may contact me about this"
                checked={mayContact}
                onChange={(e) => {
                  setMayContact(e.currentTarget.checked);
                }}
              />
              <Checkbox
                label="Keep this between me and a pastor"
                description="Only those your church has named to read confidential requests will see it."
                checked={confidential}
                onChange={(e) => {
                  setConfidential(e.currentTarget.checked);
                }}
              />
              <Group justify="flex-end">
                <Button
                  disabled={body.trim() === ""}
                  loading={submit.isPending}
                  onClick={() => {
                    submit.mutate(
                      { body, mayContact, confidential },
                      {
                        onSuccess: () => {
                          setBody("");
                          setConfidential(false);
                          bodyRef.current?.focus();
                          notifications.show({
                            color: "teal",
                            title: "Your church has it",
                            message: "Someone will pick it up. You can see where it stands below.",
                          });
                        },
                        onError: (error) => {
                          const described = describeRpcError(error);
                          notifications.show({
                            color: "red",
                            title: "Not sent",
                            message: described.message,
                          });
                        },
                      },
                    );
                  }}
                >
                  Send
                </Button>
              </Group>
            </Stack>
          </Card>
          <section aria-labelledby="care-mine">
            <Title order={2} size="h4" id="care-mine" mb="xs">
              What you asked
            </Title>
            {mine.data.requests.length === 0 ? (
              <Text size="sm" c="dimmed">
                Nothing yet.
              </Text>
            ) : (
              <Stack gap="sm">
                {mine.data.requests.map((r) => {
                  const s = statusWords(r);
                  return (
                    <Card key={r.id} data-care-request={r.id}>
                      <Stack gap="xs">
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                            {r.body}
                          </Text>
                          <Badge variant="light" color={s.color} style={{ flexShrink: 0 }}>
                            {s.label}
                          </Badge>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" c="dimmed">
                            {new Date(r.createdAt).toLocaleDateString(DISPLAY_LOCALE, {
                              day: "numeric",
                              month: "short",
                            })}
                            {r.mayContact ? "" : " · prayer only"}
                          </Text>
                          {r.confidential ? (
                            <Badge
                              variant="light"
                              color={semanticColor("special")}
                              size="sm"
                              leftSection={<IconLock size={10} />}
                            >
                              Confidential
                            </Badge>
                          ) : null}
                        </Group>
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </section>
        </Stack>
      )}
    </>
  );
}
